import React, {
  ReactNode,
  useMemo,
  MouseEvent,
} from 'react';
import clsx from 'clsx';
import keyBy from 'lodash/keyBy';
import MenuItem from '@mui/material/MenuItem';
import Select, {
  SelectChangeEvent,
} from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ToolTip from 'core/components/Tooltip';
import Pill from 'core/components/Pill';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  notEmptyString,
} from 'utils/misc';

type Parts = 'root' | 'placeholder';

type Props<T> = {
  value: string[];
  onChange: (e: string[]) => void;
  options: T[];
  renderOption?: (option: T) => ReactNode | string;
  getItemLabel?: (option: T) => string;
  getItemKey?: (option: T) => string;
  placeholder?: string;
  className?: string;
  controlClassName?: string;
  disabled?: boolean;
  error?: boolean;
  id: string;
  includeAll?: boolean;
  allCaption?: string;
  selectAllCaption?: string;
  hasClear?: boolean;
  classes?: Partial<Record<Parts, string>>;
  renderValue?: (onRemove: (key?: string | null) => void, value: string[]) => ReactNode;
};

const defaultOptionResolver = (option: any) => (option as string);

type Option = {
  label: string;
  key: string;
};

type OptionListTooltipProps = {
  options: Option[];
  onRemove?: (key?: string | null) => void;
};

function OptionListTooltip({ options, onRemove }: OptionListTooltipProps) {
  return (
    <div className="flex flex-col space-y-2 p-2">
      {options.map((option) => (
        <Pill
          id={option?.key}
          label={option?.label}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

export default function MultiSelect<T>({
  onChange,
  value = [],
  options = [],
  renderOption = defaultOptionResolver,
  getItemLabel = defaultOptionResolver,
  getItemKey = defaultOptionResolver,
  placeholder,
  className,
  controlClassName,
  disabled,
  error,
  includeAll,
  allCaption = 'All',
  selectAllCaption = 'Select All',
  hasClear = false,
  renderValue,
  id,
  classes,
  ...props
}: Props<T>) {
  const wrapperClass = clsx(
    'flex flex-col space-y-3 relative',
    className,
    { 'has-error': error },
  );
  const selectClasses = clsx(
    'populate-select !rounded',
    controlClassName,
    { disabled },
  );
  const remainder = value?.slice(1);
  const optionMap = useMemo(() => keyBy(options, getItemKey), [options, getItemKey]);
  const checked = useMemo(() => new Set<string>(value), [value]);
  const allChecked = useMemo(() => (
    options.length > 0 && options.every((option) => checked.has(getItemKey(option)))
  ), [checked, options]);

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    const { target: { value } } = e;
    onChange(value as string[]);
  };

  const handleSelectAll = () => {
    const newValue = allChecked ? [] : options.map(getItemKey).filter(notEmptyString);

    if (newValue.length > 0 || allChecked) {
      setTimeout(() => onChange(newValue), 0);
    }
  };

  const handleIndividualOption = (option: T) => {
    const optionKey = getItemKey(option);
    if (notEmptyString(optionKey)) {
      const isSelected = value.includes(optionKey);
      const newValue = isSelected
        ? value.filter((v) => v !== optionKey)
        : [...value, optionKey];

      setTimeout(() => onChange(newValue), 0);
    }
  };

  const handleClearAll = () => {
    setTimeout(() => onChange([]), 0);
  };

  const onRemove = (key?: number | string | null) => {
    const index = value.findIndex(
      (item) => getItemKey(optionMap[item]) === key,
    );

    onChange([
      ...value.slice(0, index),
      ...value.slice(index + 1),
    ]);
  };

  return (
    <div className={wrapperClass}>
      <Select<string[]>
        {...props}
        id={id}
        className={selectClasses}
        multiple
        value={value}
        onChange={handleChange}
        displayEmpty
        MenuProps={{
          PopoverClasses: {
            paper: '!w-56',
          },
        }}
        IconComponent={KeyboardArrowDownIcon}
        renderValue={() => renderValue?.(onRemove, value) ?? (
          <div className="flex flex-wrap space-x-1">
            {value.length > 0 && !allChecked && (
              <>
                {(() => {
                  const firstKey = value.find((k) => !!optionMap[k]) ?? value[0];
                  const firstOpt = optionMap[firstKey];
                  const firstLabel = firstOpt ? (getItemLabel(firstOpt) ?? firstKey) : firstKey;
                  return (
                    <Pill
                      id={firstKey}
                      label={firstLabel}
                      onRemove={onRemove}
                    />
                  );
                })()}
                {value.length > 1 && (
                  <ToolTip
                    title={(
                      <OptionListTooltip
                        options={(remainder.filter((k) => !!optionMap[k])).map((optionKey) => {
                          const opt = optionMap[optionKey];
                          return {
                            label: opt ? (getItemLabel(opt) ?? optionKey) : optionKey,
                            key: optionKey,
                          };
                        })}
                        onRemove={onRemove}
                      />
                    )}
                    isInteractive
                  >
                    <span className="!cursor-pointer !pointer-events-auto">
                      <Pill label={`+${remainder.length}`} />
                    </span>
                  </ToolTip>
                )}
              </>
            )}
            {allChecked && (
              <Chip
                label={allCaption}
                className="!h-auto !p-0.5 !text-xs !bg-gray-200 !text-black"
              />
            )}
            {value.length === 0 && (
              <div className={clsx('font-semibold', classes?.placeholder)}>
                {placeholder}
              </div>
            )}
          </div>
        )}
      >
        {includeAll && (
          <MenuItem
            disabled={options.length === 0}
            className={clsx('populate-menu-item !bg-transparent !text-xs !p-0', { 'Mui-selected': allChecked })}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAll();
            }}
          >
            <Checkbox
              checked={allChecked}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectAll();
              }}
              className={clsx('!p-1.5', {
                '!text-blue-600': allChecked,
              })}
            />
            {selectAllCaption}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={getItemKey(option)}
            className="populate-menu-item !bg-transparent !text-xs !p-0 !whitespace-pre-wrap"
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleIndividualOption(option);
            }}
          >
            <Checkbox
              checked={value?.includes(getItemKey(option))}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleIndividualOption(option);
              }}
              className={clsx('!p-1.5', {
                '!text-blue-600': value.includes(getItemKey(option)),
              })}
            />
            {getItemLabel(option)}
          </MenuItem>
        ))}
        {hasClear && (
          <MenuItem
            className="!py-2"
            disabled={value.length === 0}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleClearAll();
            }}
          >
            Clear All
          </MenuItem>
        )}
      </Select>
    </div>
  );
}
